import { PaymentModuleId } from '@foundation/apis/payments';
import { SupportedLanguage } from '@foundation/localization';
import Clarity from '@microsoft/clarity';
import type { OnApproveData } from '@paypal/paypal-js';
import {
  PayPalButtons,
  PayPalScriptProvider,
  usePayPalScriptReducer,
} from '@paypal/react-paypal-js';
import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import { useMemo, useState, JSX } from 'react';
import { useSearchParams } from 'react-router-dom';

import { LoadingSpinner, LoadingSpinnerSize } from '@/components/common';
import { AlertError } from '@/components/common/alerts';
import { RedirectStatus } from '@/components/contexts/params';
import { useUserFriendlyPaymentModuleName } from '@/hooks/useUserFriendlyPaymentModuleName';
import { triggerErrorToast, triggerInfoToast } from '@/libs/toasts';
import { PaymentIntentFlow } from '@/schemas/PaymentIntent';
import BraintreePayPalService from '@/services/braintree/BraintreePayPalService';
import Logger, { LoggerOrigin } from '@/services/Logger';
import NavigationService, {
  InternalPage,
} from '@/services/navigation/NavigationService';
import PaymentStore from '@/services/payments/PaymentStore';
import TelemetryService from '@/services/telemetry/TelemetryService';
import { getQaTestingProps, QaTestingElements } from '@/utils/qaTesting';

interface IPayWithBraintreePayPalBtnProps {
  readonly navigation?: JSX.Element | null;
}

export function PayWithBraintreePayPalBtn({
  navigation,
}: IPayWithBraintreePayPalBtnProps) {
  const {
    i18n: { language },
  } = useTranslation();

  const scriptOptions = useMemo(
    () =>
      BraintreePayPalService.getPayPalScriptOptions(
        language as SupportedLanguage,
      ),
    [language],
  );

  return (
    <PayPalScriptProvider options={scriptOptions}>
      <PayPalButtonWrapper navigation={navigation} />
    </PayPalScriptProvider>
  );
}

function PayPalButtonWrapper({ navigation }: IPayWithBraintreePayPalBtnProps) {
  const { userId } = useAuth();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const paymentModuleT = useUserFriendlyPaymentModuleName();
  const [{ isPending }] = usePayPalScriptReducer();
  const [searchParams, setSearchParams] = useSearchParams();

  const [err, setErr] = useState<Error>();

  const buttonStyle = BraintreePayPalService.getPayPalBtnStyle(
    PaymentStore.theme,
  );

  const billingAgreement = useMemo(() => {
    return () =>
      BraintreePayPalService.createBillingAgreement(
        language as SupportedLanguage,
      );
  }, [language]);

  const proceedToAuthorize = async (data: OnApproveData) => {
    const currentPage = NavigationService.getInternalPage();
    NavigationService.navigateTo({ page: InternalPage.StatusProcessing });

    return BraintreePayPalService.getTokenizedPayloadNonce(data)
      .then((nonce) =>
        PaymentStore.flow === PaymentIntentFlow.Manage
          ? BraintreePayPalService.tokenize(nonce, userId)
          : BraintreePayPalService.authorize(nonce),
      )
      .then(() => {
        if (PaymentStore.flow === PaymentIntentFlow.Manage) {
          searchParams.set('status', RedirectStatus.Success);
          setSearchParams(searchParams);
          window.location.reload();
        } else {
          NavigationService.navigateTo({ page: InternalPage.StatusSuccess });
        }
        Clarity.setTag('pay', [
          RedirectStatus.Success,
          paymentModuleT(PaymentModuleId.BraintreePayPal),
        ]);
      })
      .catch((e: Error) => {
        NavigationService.navigateTo(currentPage);
        TelemetryService.trackException(e, 'BraintreePayPalBtn');
        Logger.error(
          LoggerOrigin.ReactComponent,
          'PayPal authorization failed.',
          e,
        );
        Clarity.setTag('pay', [
          RedirectStatus.Failure,
          paymentModuleT(PaymentModuleId.BraintreePayPal),
        ]);
        triggerErrorToast(
          t('Something went wrong!'),
          t(
            'Operation failed. Please try again with a different payment method.',
          ),
        );
        setErr(e);
      });
  };

  if (err) {
    return (
      <AlertError
        title={t('Something went wrong!')}
        description={t(
          'Operation failed. Please try again with a different payment method.',
        )}
      />
    );
  }

  if (isPending) {
    return (
      <div className="h-14">
        <LoadingSpinner size={LoadingSpinnerSize.Small} />
      </div>
    );
  }

  return (
    <div
      className="h-14 flex flex-row space-x-2"
      {...getQaTestingProps(QaTestingElements.PaypalPayButton)}
    >
      {navigation}
      <div className="w-full">
        <PayPalButtons
          style={buttonStyle}
          forceReRender={[PaymentStore.theme]}
          createBillingAgreement={billingAgreement}
          onApprove={proceedToAuthorize}
          onError={(err) => {
            triggerErrorToast(
              t('Something went wrong!'),
              t('Select a new payment method and proceed to pay.'),
            );
            Logger.error(LoggerOrigin.PayPalButton, 'onError', err);
            TelemetryService.trackException(undefined, 'PayPalButtons', {
              type: 'PayPalButtons.onError',
              err: err,
            });
          }}
          onCancel={() => {
            triggerInfoToast(
              t('Select a new payment method and proceed to pay.'),
            );
            Clarity.setTag('pay', [
              RedirectStatus.Canceled,
              paymentModuleT(PaymentModuleId.BraintreePayPal),
            ]);
          }}
        />
      </div>
    </div>
  );
}
